export type VehicleClass =
  | 'S' | 'A' | 'B' | 'C' | 'D'
  | 'Bike-S' | 'Bike-A' | 'Bike-B' | 'Bike-C' | 'Bike-D'
  | 'Police';

export type ProgressStatus =
  | 'To Do'
  | 'In Progress / Active Editing'
  | 'In Review'
  | 'Handling Approved'
  | 'Pushed'
  | 'Merged into Live';

export interface Tag {
  id: number;
  name: string;
  colour: string;
  vehicle_count?: number;
}

export interface Dealership {
  id: number;
  name: string;
  vehicle_count?: number;
}

export interface Vehicle {
  id: number;
  name: string | null;
  spawncode: string;
  brand: string | null;
  category: string | null;
  vehicle_class: VehicleClass;
  price: number | null;
  link: string | null;
  image_key: string | null;
  image_url: string | null;
  doors: number | null;
  seats: number | null;
  gears: number | null;
  top_speed: number | null;
  zero_to_sixty: number | null;
  zero_to_hundred: number | null;
  tiers: string;
  dealership_id: number | null;
  dealership_name?: string | null;
  progress: ProgressStatus;
  boosting_exclusive: number;
  rp_exclusive: number;
  created_at: string;
  updated_at: string;
  tags: Tag[];
  total_handling_time?: number;
}

export interface HandlingLogEntry {
  id: number;
  vehicle_id: number;
  notes: string | null;
  time_spent_minutes: number;
  logged_at: string;
}

export interface HandlingRevision {
  id: number;
  vehicle_id: number;
  handling_xml: string;
  change_note: string | null;
  changed_by: string;
  created_at: string;
}

export interface AuditLogEntry {
  id: number;
  vehicle_id: number;
  changed_by: string;
  changes: string;
  notes: string | null;
  changed_at: string;
}

export interface User {
  id: number;
  username: string;
}

// ─── Constants ────────────────────────────────────────────────────────────────

export const PROGRESS_STATUSES: ProgressStatus[] = [
  'To Do',
  'In Progress / Active Editing',
  'In Review',
  'Handling Approved',
  'Pushed',
  'Merged into Live',
];

export const VEHICLE_CLASSES: VehicleClass[] = [
  'S', 'A', 'B', 'C', 'D',
  'Police',
  'Bike-S', 'Bike-A', 'Bike-B', 'Bike-C', 'Bike-D',
];

export const CLASS_COLOURS: Record<VehicleClass, { bg: string; text: string }> = {
  S:        { bg: '#C9A84C', text: '#0D0D0D' },  // Gold
  A:        { bg: '#C8102E', text: '#FFFFFF' },  // Red
  B:        { bg: '#8B5CF6', text: '#FFFFFF' },  // Purple
  C:        { bg: '#3B82F6', text: '#FFFFFF' },  // Blue
  D:        { bg: '#22C55E', text: '#0D0D0D' },  // Green
  Police:   { bg: '#1E3A5F', text: '#FFFFFF' },  // Navy
  'Bike-S': { bg: '#F59E0B', text: '#0D0D0D' },  // Amber
  'Bike-A': { bg: '#F97316', text: '#0D0D0D' },  // Orange
  'Bike-B': { bg: '#EC4899', text: '#FFFFFF' },  // Pink
  'Bike-C': { bg: '#06B6D4', text: '#0D0D0D' },  // Cyan
  'Bike-D': { bg: '#94A3B8', text: '#0D0D0D' },  // Slate
};

export function classLabel(vc: VehicleClass): string {
  if (vc === 'Police') return 'Police';
  if (vc.startsWith('Bike-')) return `Bike ${vc.slice(5)}`;
  return `${vc} Class`;
}

export const TIER_RULES: Record<VehicleClass, string[]> = {
  S:        ['Tier 1', 'Tier 2', 'Tier 3'],
  A:        ['Tier 1', 'Tier 2', 'Tier 3'],
  B:        ['Tier 1', 'Tier 2'],
  C:        ['Tier 1', 'Tier 2'],
  D:        ['Tier 1', 'Tier 2'],
  Police:   [],
  'Bike-S': ['Tier 1', 'Tier 2'],
  'Bike-A': ['Tier 1', 'Tier 2'],
  'Bike-B': ['Tier 1', 'Tier 2'],
  'Bike-C': ['Tier 1', 'Tier 2'],
  'Bike-D': ['Tier 1', 'Tier 2'],
};

export const CATEGORIES = [
  'Police', 'EMS', 'SWAT / Armoured Emergency', 'Fire and Rescue',
  'Prison Transport / DOC', 'Tow Trucks', 'Bus', 'Compact', 'Sedans',
  'Hatchbacks', 'Coupes', 'Wagons', 'Sports', 'Sports Classic', 'Supercar',
  'Hypercar', 'Muscle', 'Drift Car', 'SUVs', 'Offroad', 'Trucks', 'Vans',
  'Utility Vehicle', 'Construction Vehicle', 'Motorcycle', 'Bicycle',
  'Airplane', 'Helicopter', 'Boat', 'Taxi',
] as const;

export const CATEGORY_COLOURS: Record<string, string> = {
  'Police':                   '#3B82F6',
  'EMS':                      '#10B981',
  'SWAT / Armoured Emergency':'#556B2F',
  'Fire and Rescue':          '#EF4444',
  'Prison Transport / DOC':   '#1E3A5F',
  'Tow Trucks':               '#F59E0B',
  'Bus':                      '#D97706',
  'Compact':                  '#F4A46A',
  'Sedans':                   '#8FAF7E',
  'Hatchbacks':               '#98D8C8',
  'Coupes':                   '#14B8A6',
  'Wagons':                   '#9E9E9E',
  'Sports':                   '#F97316',
  'Sports Classic':           '#A8B92C',
  'Supercar':                 '#8B5CF6',
  'Hypercar':                 '#EC4899',
  'Muscle':                   '#C2410C',
  'Drift Car':                '#06B6D4',
  'SUVs':                     '#C084FC',
  'Offroad':                  '#B5975A',
  'Trucks':                   '#78716C',
  'Vans':                     '#A78BFA',
  'Utility Vehicle':          '#9B9141',
  'Construction Vehicle':     '#B5A020',
  'Motorcycle':               '#FF7043',
  'Bicycle':                  '#93C5FD',
  'Airplane':                 '#38BDF8',
  'Helicopter':               '#64748B',
  'Boat':                     '#0EA5E9',
  'Taxi':                     '#FBBF24',
};

export const PROGRESS_BADGE_CLASS: Record<ProgressStatus, string> = {
  'To Do':                       'badge-todo',
  'In Progress / Active Editing':'badge-inprogress',
  'In Review':                   'badge-inreview',
  'Handling Approved':           'badge-approved',
  'Pushed':                      'badge-pushed',
  'Merged into Live':            'badge-merged',
};

/** Single source of truth for progress status colours — used in badges, filter pills, kanban columns */
export const PROGRESS_COLOURS: Record<ProgressStatus, { bg: string; text: string; label: string }> = {
  'To Do':                        { bg: '#374151', text: '#E5E7EB', label: 'To Do' },
  'In Progress / Active Editing': { bg: '#C9A84C', text: '#0D0D0D', label: 'In Progress' },
  'In Review':                    { bg: '#E8A0B0', text: '#1A1A1A', label: 'In Review' },
  'Handling Approved':            { bg: '#22C55E', text: '#0D0D0D', label: 'Approved' },
  'Pushed':                       { bg: '#C8102E', text: '#FFFFFF', label: 'Pushed' },
  'Merged into Live':             { bg: '#6366F1', text: '#FFFFFF', label: 'Merged' },
};

// Filter state type
export interface FilterState {
  search: string;
  vehicle_class: string[];
  category: string[];
  tier: string[];
  progress: string[];
  dealership_id: string;
  dealership_available: boolean;
  rp_exclusive: boolean;
  boosting_exclusive: boolean;
  min_doors: string;
  max_doors: string;
  min_seats: string;
  max_seats: string;
  min_speed: string;
  max_speed: string;
  tag_ids: string[];
}

export const DEFAULT_FILTERS: FilterState = {
  search: '',
  vehicle_class: [],
  category: [],
  tier: [],
  progress: [],
  dealership_id: '',
  dealership_available: false,
  rp_exclusive: false,
  boosting_exclusive: false,
  min_doors: '',
  max_doors: '',
  min_seats: '',
  max_seats: '',
  min_speed: '',
  max_speed: '',
  tag_ids: [],
};
